import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Modal, Space, Spin, Typography, message } from 'antd';
import { ArrowLeftOutlined, ThunderboltOutlined } from '@ant-design/icons';
import MDEditor from '@uiw/react-md-editor';
import dayjs from 'dayjs';
import {
    generateMonthlyReport,
    getMonthlyReport,
    saveMonthlyReport,
} from './monthlyReportsService';

const MonthlyReportEditor: React.FC = () => {
    const { login } = useParams<{ login: string }>();
    const [searchParams] = useSearchParams();
    const month = useMemo(() => searchParams.get('month') ?? '', [searchParams]);

    const [content, setContent] = useState<string>('');
    const [generatedAt, setGeneratedAt] = useState<string | null>(null);
    const [lastEditedAt, setLastEditedAt] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [hasSaved, setHasSaved] = useState<boolean>(false);
    const [generating, setGenerating] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!login || !month) return;
        setLoading(true);
        setError(null);
        try {
            const report = await getMonthlyReport(month, login);
            setContent(report.content);
            setGeneratedAt(report.generatedAt);
            setLastEditedAt(report.lastEditedAt);
            setHasSaved(true);
        } catch (err: any) {
            if (err?.response?.status === 404) {
                setHasSaved(false);
                setContent('');
            } else {
                setError(err?.response?.data?.message || err?.message || 'Unknown error');
            }
        } finally {
            setLoading(false);
        }
    }, [login, month]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        document.title = `${login} · ${month} · Sonat Support`;
    }, [login, month]);

    const runGenerate = useCallback(async () => {
        if (!login || !month) return;
        setGenerating(true);
        setError(null);
        try {
            const generated = await generateMonthlyReport(month, login);
            setContent(generated.content);
            setGeneratedAt(generated.generatedAt);
            message.success('Report generated. Click Save to persist.');
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Generation failed');
        } finally {
            setGenerating(false);
        }
    }, [login, month]);

    const handleGenerateClick = () => {
        if (hasSaved) {
            Modal.confirm({
                title: 'Overwrite the current report?',
                content: 'The existing markdown will be replaced with a fresh generation. You can still edit it before saving.',
                okText: 'Generate',
                onOk: runGenerate,
            });
        } else {
            runGenerate();
        }
    };

    const handleSave = async () => {
        if (!login || !month) return;
        setSaving(true);
        setError(null);
        try {
            const saved = await saveMonthlyReport(month, login, content, generatedAt);
            setLastEditedAt(saved.lastEditedAt);
            setHasSaved(true);
            message.success('Saved');
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: 16, textAlign: 'center' }}>
                <Spin />
            </div>
        );
    }

    return (
        <div style={{ padding: 16 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Space>
                    <Link to={`/team-insights/reports?month=${month}`}>
                        <Button icon={<ArrowLeftOutlined />}>Back</Button>
                    </Link>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                        {login} · {month}
                    </Typography.Title>
                </Space>
                <Space>
                    <Button
                        icon={<ThunderboltOutlined />}
                        onClick={handleGenerateClick}
                        loading={generating}
                    >
                        {hasSaved ? 'Regenerate' : 'Generate'}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSave}
                        loading={saving}
                        disabled={!content}
                    >
                        Save
                    </Button>
                </Space>
            </Space>

            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                {hasSaved && lastEditedAt && `Last saved ${dayjs(lastEditedAt).format('YYYY-MM-DD HH:mm')}`}
                {generatedAt && hasSaved && ' · '}
                {generatedAt && `Generated ${dayjs(generatedAt).format('YYYY-MM-DD HH:mm')}`}
                {!hasSaved && !generatedAt && 'No saved report yet — click Generate to draft one.'}
            </Typography.Text>

            {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />}

            <div data-color-mode="light">
                <MDEditor
                    value={content}
                    onChange={(val) => setContent(val ?? '')}
                    height={600}
                    preview={hasSaved || !!content ? 'live' : 'edit'}
                />
            </div>
        </div>
    );
};

export default MonthlyReportEditor;
